import {
  ChevronLeft,
  ChevronRight,
  Cloud,
  GitBranch,
  Settings,
} from 'lucide-react'
import { useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { Tooltip } from '@/components/ui/Tooltip'
import { brandFor } from '@/features/lifecycle/components/view/brands'
import { BrandLogo } from '@/features/lifecycle/components/view/LifecycleDataGroup'

const configurationLinks = [
  {
    label: 'Kong Konnect',
    to: '/config/kong',
    icon: Cloud,
    brand: brandFor('kong'),
  },
  {
    label: 'Apigee X',
    to: '/config/apigee',
    icon: Cloud,
    brand: brandFor('apigee'),
  },
]

const scmPath = '/config/scm'
const scmProviders = [
  { label: 'GitHub', type: 'GITHUB', brand: brandFor('github') },
  { label: 'GitLab', type: 'GITLAB', brand: brandFor('gitlab') },
  { label: 'Bitbucket', type: 'BITBUCKET', brand: brandFor('bitbucket') },
]

// Width of the SCM submenu (w-44) plus its small hover gap.
const scmSubmenuWidth = 180

const menuItemClass =
  'group/item flex min-h-10 items-center gap-3 rounded-md px-3 py-2 text-sm text-secondary no-underline transition-colors hover:bg-inset hover:text-primary aria-[current=page]:bg-accent-faint aria-[current=page]:text-accent'

const menuIconClass = 'transition-colors group-hover/item:text-accent'

export function ConfigMenu() {
  const id = useId()
  const { pathname, search } = useLocation()
  const scmType = new URLSearchParams(search).get('type') ?? 'GITHUB'
  const scmRow = useRef<HTMLDivElement>(null)
  const [scmSide, setScmSide] = useState<'right' | 'left'>('right')

  // Open the SCM submenu to the right; flip to the left when it would run off the screen.
  function placeScmSubmenu() {
    const row = scmRow.current?.getBoundingClientRect()
    if (!row) return
    const fitsRight =
      row.right + scmSubmenuWidth <= document.documentElement.clientWidth - 8
    setScmSide(fitsRight ? 'right' : 'left')
  }
  const trigger = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  useLayoutEffect(() => {
    if (!open) return
    function reposition() {
      const anchor = trigger.current?.getBoundingClientRect()
      if (!anchor) return
      const width = Math.min(224, document.documentElement.clientWidth - 24)
      setPosition({
        top: anchor.bottom + 6,
        left: Math.max(
          12,
          Math.min(
            anchor.right - width,
            document.documentElement.clientWidth - width - 12,
          ),
        ),
      })
    }
    function dismissOnEscape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      event.preventDefault()
      document.getElementById(id)?.hidePopover()
      trigger.current?.focus({ preventScroll: true })
    }
    reposition()
    window.addEventListener('keydown', dismissOnEscape)
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('keydown', dismissOnEscape)
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, id])

  return (
    <>
      <Tooltip label="Configurations" side="bottom" delay={400} disabled={open}>
        <button
          ref={trigger}
          type="button"
          popoverTarget={id}
          aria-label="Configurations"
          aria-expanded={open}
          aria-controls={id}
          aria-haspopup="dialog"
          className="grid size-8 shrink-0 cursor-pointer place-items-center rounded-lg bg-raised/50 text-primary transition hover:border-accent-border/50 hover:bg-raised"
        >
          <Settings size={16} aria-hidden="true" />
        </button>
      </Tooltip>
      {createPortal(
        <div
          id={id}
          popover="auto"
          role="dialog"
          aria-label="Configurations"
          onToggle={(event) => setOpen(event.newState === 'open')}
          className="fixed m-0 w-56 overflow-visible rounded-lg border border-border-strong bg-raised p-0 text-primary shadow-lg"
          style={position}
        >
          <div className="border-b border-border-default px-4 py-3 text-xs font-semibold tracking-[0.18em] text-muted uppercase">
            Configurations
          </div>
          <nav className="p-1" aria-label="Configuration profiles">
            {configurationLinks.map(({ label, to, icon: Icon, brand }) => (
              <Link
                key={to}
                to={to}
                aria-current={
                  pathname === to || pathname.startsWith(`${to}/`)
                    ? 'page'
                    : undefined
                }
                onClick={() => document.getElementById(id)?.hidePopover()}
                className={menuItemClass}
              >
                {brand ? (
                  <span className="inline-flex text-primary">
                    <BrandLogo brand={brand} className="size-4" />
                  </span>
                ) : (
                  <Icon
                    size={16}
                    aria-hidden="true"
                    className={menuIconClass}
                  />
                )}
                {label}
              </Link>
            ))}

            <div
              ref={scmRow}
              onMouseEnter={placeScmSubmenu}
              onFocus={placeScmSubmenu}
              className="group/scm relative"
            >
              <Link
                to={`${scmPath}?type=GITHUB`}
                aria-haspopup="menu"
                aria-current={pathname === scmPath ? 'page' : undefined}
                onClick={() => document.getElementById(id)?.hidePopover()}
                className={menuItemClass}
              >
                <GitBranch
                  size={16}
                  aria-hidden="true"
                  className={menuIconClass}
                />
                SCM
                {scmSide === 'right' ? (
                  <ChevronRight
                    size={16}
                    aria-hidden="true"
                    className="ml-auto text-muted"
                  />
                ) : (
                  <ChevronLeft
                    size={16}
                    aria-hidden="true"
                    className="ml-auto text-muted"
                  />
                )}
              </Link>
              <div
                className={`absolute top-0 hidden group-focus-within/scm:block group-hover/scm:block ${
                  scmSide === 'right' ? 'left-full pl-1' : 'right-full pr-1'
                }`}
              >
                <div
                  role="menu"
                  aria-label="SCM providers"
                  className="w-44 rounded-lg border border-border-strong bg-raised p-1 shadow-lg"
                >
                  {scmProviders.map(({ label, type, brand }) => (
                    <Link
                      key={type}
                      to={`${scmPath}?type=${type}`}
                      role="menuitem"
                      aria-current={
                        pathname === scmPath && scmType === type
                          ? 'page'
                          : undefined
                      }
                      onClick={() => document.getElementById(id)?.hidePopover()}
                      className={menuItemClass}
                    >
                      {brand && (
                        // Brand logos keep their own colour; GitHub's has none, so pin it to the text colour.
                        <span className="inline-flex text-primary">
                          <BrandLogo brand={brand} className="size-4" />
                        </span>
                      )}
                      {label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </nav>
        </div>,
        document.body,
      )}
    </>
  )
}
